import { create } from 'zustand'
import type { User } from '@/types/auth'
import { supabase } from '@/lib/supabase'
import { authApi } from '@/services/api/auth'
import { updateSessionCache } from '@/services/api/client'

interface AuthStore {
  user: User | null
  session: any | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, fullName?: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  initialize: () => Promise<void>
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,

  initialize: async () => {
    set({ isLoading: true })
    
    // Get initial session with timeout to avoid hanging
    let session = null
    try {
      const sessionPromise = supabase.auth.getSession()
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session timeout')), 5000)
      )
      const result = await Promise.race([sessionPromise, timeoutPromise]) as any
      session = result?.data?.session || null
    } catch (error) {
      console.warn('Failed to get initial session:', error)
      session = null
    }
    
    // Update cache
    updateSessionCache(session)
    
    if (session) {
      // Fetch user profile from backend
      try {
        const user = await authApi.me()
        set({
          user,
          session,
          isAuthenticated: true,
          isLoading: false,
        })
      } catch (error) {
        // If backend call fails, sign out from Supabase
        await supabase.auth.signOut()
        updateSessionCache(null)
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    } else {
      set({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange(async (event, session) => {
      // Update cache whenever auth state changes
      updateSessionCache(session)
      
      if (event === 'SIGNED_IN' && session) {
        try {
          const user = await authApi.me()
          set({
            user,
            session,
            isAuthenticated: true,
          })
        } catch (error) {
          console.error('Failed to fetch user profile:', error)
        }
      } else if (event === 'SIGNED_OUT') {
        // Disconnect all WebSocket connections
        import('@/services/websocket').then(({ default: wsService }) => {
          wsService.disconnectAll()
        })
        
        set({
          user: null,
          session: null,
          isAuthenticated: false,
        })
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('🔄 Token refreshed, updating session')
        set({ session })
      }
    })
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      // Fetch user profile from backend
      const user = await authApi.me()

      set({
        user,
        session: data.session,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error: any) {
      set({ isLoading: false })
      throw new Error(error.message || 'Failed to login')
    }
  },

  register: async (email: string, password: string, fullName?: string) => {
    set({ isLoading: true })
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      })

      if (error) throw error

      // If email confirmation is required, session might be null
      if (data.session) {
        // Fetch user profile from backend
        const user = await authApi.me()
        set({
          user,
          session: data.session,
          isAuthenticated: true,
          isLoading: false,
        })
      } else {
        // Email confirmation required
        set({ isLoading: false })
        throw new Error('Please check your email to confirm your account')
      }
    } catch (error: any) {
      set({ isLoading: false })
      throw new Error(error.message || 'Failed to register')
    }
  },

  logout: async () => {
    set({ isLoading: true })
    
    // Disconnect all WebSocket connections
    import('@/services/websocket').then(({ default: wsService }) => {
      wsService.disconnectAll()
    })
    
    await supabase.auth.signOut()
    
    set({
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: false,
    })
  },

  checkAuth: async () => {
    const { data: { session } } = await supabase.auth.getSession()
    
    if (!session) {
      set({ isAuthenticated: false, user: null, session: null })
      return
    }

    try {
      const user = await authApi.me()
      set({
        user,
        session,
        isAuthenticated: true,
      })
    } catch (error) {
      // If backend call fails, sign out from Supabase
      await supabase.auth.signOut()
      set({
        user: null,
        session: null,
        isAuthenticated: false,
      })
    }
  },
}))
